import { motion, useReducedMotion, type Variants } from "motion/react";
import { type ComponentProps, useRef } from "react";
import { toEntranceVariant, useEntrance } from "@/src/common/hooks/use-entrance";

export const REVEAL_EASE: readonly [number, number, number, number] = [0.16, 1, 0.3, 1];

export const REVEAL_AMOUNT = 0.1;

const REVEAL_DURATION = 0.6;

const STAGGER_STEP = 0.08;

export function createRevealVariants(delay = 0, reduced = false): Variants {
	return {
		hidden: { opacity: reduced ? 1 : 0, y: reduced ? 0 : 24, transition: { duration: 0 } },
		visible: {
			opacity: 1,
			y: 0,
			transition: reduced ? { duration: 0 } : { duration: REVEAL_DURATION, ease: REVEAL_EASE, delay },
		},
	};
}

export function createStaggerContainer(reduced = false, stagger = STAGGER_STEP, delayChildren = 0): Variants {
	return {
		hidden: {},
		visible: { transition: reduced ? { duration: 0 } : { staggerChildren: stagger, delayChildren } },
	};
}

export function createStaggerItem(reduced = false, distance = 16): Variants {
	return {
		hidden: { opacity: reduced ? 1 : 0, y: reduced ? 0 : distance, transition: { duration: 0 } },
		visible: {
			opacity: 1,
			y: 0,
			transition: reduced ? { duration: 0 } : { duration: REVEAL_DURATION, ease: REVEAL_EASE },
		},
	};
}

interface AnimatedSectionProps extends ComponentProps<typeof motion.section> {
	delay?: number;
}

export function AnimatedSection({ children, delay = 0, className, ...rest }: AnimatedSectionProps) {
	const ref = useRef<HTMLElement>(null);
	const reduced = useReducedMotion() === true;
	const entrance = useEntrance(ref, { amount: REVEAL_AMOUNT, disabled: reduced });
	const variants = createRevealVariants(delay, reduced);

	return (
		<motion.section ref={ref} className={className} initial={false} animate={toEntranceVariant(entrance)} variants={variants} {...rest}>
			{children}
		</motion.section>
	);
}
